import type { Locale } from '../api/types';

export const DEFAULT_TIME_ZONE = 'Asia/Taipei';

function safeZone(timeZone: string | undefined): string {
  if (!timeZone) return DEFAULT_TIME_ZONE;
  try {
    new Intl.DateTimeFormat('en', { timeZone });
    return timeZone;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

/** Date and time in the workspace time zone (NFR-I18N-02). */
export function formatDateTime(
  value: string | Date | null | undefined,
  locale: Locale,
  timeZone?: string,
): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    timeZone: safeZone(timeZone),
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

export function formatDate(
  value: string | Date | null | undefined,
  locale: Locale,
  timeZone?: string,
): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    timeZone: safeZone(timeZone),
    dateStyle: 'medium',
  }).format(d);
}

/**
 * A calendar day `YYYY-MM-DD` that is already in the workspace time zone (dashboard trend):
 * formatted as is, without shifting it through another zone.
 */
export function formatDay(day: string, locale: Locale, short = false): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return new Intl.DateTimeFormat(
    locale,
    short
      ? { timeZone: 'UTC', month: 'short', day: 'numeric' }
      : { timeZone: 'UTC', dateStyle: 'medium' },
  ).format(d);
}

/** "in 3 days" / "2 days ago", rounded to whole days. */
export function formatRelativeDays(
  value: string | null | undefined,
  locale: Locale,
  now = Date.now(),
): string {
  if (!value) return '—';
  const days = Math.round((new Date(value).getTime() - now) / 86_400_000);
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(days, 'day');
}

export function formatBytes(bytes: number, locale: Locale): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: i === 0 ? 0 : 1 }).format(v)} ${units[i] ?? 'B'}`;
}

export function formatPercent(value: number | null | undefined, locale: Locale): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(
    value,
  );
}

/** Time zones for the workspace settings picker. */
export function timeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  const list = intl.supportedValuesOf?.('timeZone') ?? [DEFAULT_TIME_ZONE, 'UTC'];
  return list.includes('UTC') ? list : ['UTC', ...list];
}
